import React, { useMemo, useState } from 'react';
import { DialogContent, Grid, Typography, TextField, List, ListItem, Button, FormControl, FormControlLabel, Switch, makeStyles } from '@material-ui/core';
import EmailIcon from '@material-ui/icons/Email';

import FacebookLogo from '../../resources/facebookIcon.png';
import TwitterLogo from '../../resources/twitterIcon.png';
import { useAuthenticatedUser } from '../../utilities/useAuthenticatedUser';
import { RemoveVideoDialog } from './RemoveVideoDialog';
import { ShareVideoDialog } from './ShareVideoDialog';
import { ShareService } from '../../services/ShareService';
import { useSharingDialog } from './SharingDialog';

const useStyles = makeStyles((theme) => ({
    urlInput: {
        maxHeight: 51,
        borderRadius: '0px',
        fontSize: '12px',
        [`& fieldset`]: {
            borderRadius: 0,
        }
    },
    listHorizontal: {
        display: 'flex',
        flexDirection: 'row',
        padding: 0,
    },
    listItem: {
        width: 70,
        "&:nth-of-type(2)": {
            width: 95
        }
    },
    mobileFormLabel: {
        [theme.breakpoints.down('sm')]: {
            marginLeft: 0
        },
    },
    switchLabel: {
        fontWeight: 500,
        fontSize: 16
    },
    copyButton: {
        width: '100%',
        height: 51,
        backgroundColor: '#5bb2f6',
        marginBottom: 8,
        color: '#FFF',
        textAlign: 'center',
        verticalAlign: 'middle',
        "& span": {
            display: 'inline-block',
            marginTop: 16
        },
        cursor: 'pointer',
    },
    socialIcons: {
        width: 40,
        height: 40,
        marginTop: -5
    },
    listItemSubContainer: {
        textAlign: 'center'
    },
}));

export function RenderSharedContent(){
    const classes = useStyles();
    const user = useAuthenticatedUser();
    const sharingContext = useSharingDialog();
    const { sharedItem, sharedUrl } = sharingContext;

    const [disabled, setDisabled] = useState(false);
    const [showLinkVideo, setShowLinkVideo] = useState(false);
    const [showRemoveVideo, setShowRemoveVideo] = useState(false);

    const encodedEmailUrl = useMemo(() => {
        let emailUrl = 'mailto:?body=I want to share ';
        emailUrl += `this ${sharedItem.category}`;
        emailUrl += `  ${sharedUrl}`;
    
        emailUrl += `${sharedItem.slug}&subject=I want you to see this ${sharedItem.category}`;

        return encodeURI(emailUrl);
    }, [sharedUrl, sharedItem]) 

    const encodedFacebookUrl = useMemo(() => {
        let facebookUrl = 'https://www.facebook.com/sharer/sharer.php';
        facebookUrl += `?u=${sharedUrl}`;
        facebookUrl += `&quote=${sharedItem.name}`;
        facebookUrl += ` by ${user.firstName} ${user.lastName}`;

        return encodeURI(facebookUrl)
    }, [sharedUrl, sharedItem])

    const encodedTwitterUrl = useMemo(() => {
        let twitterUrl = 'https://twitter.com/share?ref_src=twsrc%5Etfw';
        twitterUrl += `&url=${sharedUrl}`;
        twitterUrl += `&title=${sharedItem.name}`;
        twitterUrl += ` by ${user.firstName} ${user.lastName}`;
        return encodeURI(twitterUrl);
    }, [sharedUrl, sharedItem])

    const handleCopy = () => {
        let el = document.getElementById("urlInput") as HTMLInputElement;
    
        if (!el) {
            return;
        }
    
        // handle iOS as a special case
        if (navigator.userAgent.match(/ipad|ipod|iphone/i)) {
            // save current contentEditable/readOnly status
            var editable = el.contentEditable;
            var readOnly = el.readOnly;

            // convert to editable with readonly to stop iOS keyboard opening
            el.contentEditable = "true";
            el.readOnly = true;

            // create a selectable range
            var range = document.createRange();
            range.selectNodeContents(el);

            // select the range
            var selection = window.getSelection();
            if (selection) {
            selection.removeAllRanges();
            selection.addRange(range);
            }
            el.setSelectionRange(0, 999999);

            // restore contentEditable/readOnly to original state
            el.contentEditable = editable;
            el.readOnly = readOnly;
        } else {
            el.select();
        }
    
        document.execCommand("copy");
        sharingContext.showMessage("Link copied to clipboard.");
    }

    const handleCommentsToggle = async() => {
        if(disabled) return;

        setDisabled(true);
        const response = await ShareService.saveAssetShare({
            commentsEnabled: !sharedItem.commentsEnabled,
            enabled: sharedItem.enabled,
            assetShareId: sharedItem.id,
            assetShareVideoTypeId: sharedItem.assetShareType,
            videoPath: sharedItem.videoPath
        });
        setDisabled(false);

        if(response.error) return sharingContext.onError('An unknown error occurred. Please try again.');

        sharingContext.updateSharedItem(response.data);
    }

    return (
        <DialogContent style={{ width: "100%" }}>
            <Grid container spacing={0}>
                <Grid item xs={12}>
                    <Typography style={{ marginBottom: 8 }} variant="subtitle2">Link for sharing</Typography>
                </Grid>
                <Grid item xs={9}>
                    <TextField
                        id="url"
                        value={sharedUrl}
                        fullWidth
                        InputProps={{
                            readOnly: true,
                            className: classes.urlInput,
                            id: "urlInput"
                        }}
                        variant="outlined"
                    />
                </Grid>
                <Grid item xs={3}>
                    <div className={classes.copyButton} onClick={() => handleCopy()}>
                        <span>COPY LINK</span>
                    </div>
                </Grid>
            </Grid>
            <hr />
            <Typography style={{ marginBottom: 8, marginTop: 12 }} variant="subtitle2">More sharing options</Typography>
            <div style={{ maxWidth: 300, marginBottom: 12 }}>
                <List className={classes.listHorizontal}>
                    <ListItem className={classes.listItem}>
                        <div className={classes.listItemSubContainer}>
                            <EmailIcon fontSize="large" />
                            <a href={encodedEmailUrl}>Email</a>
                        </div>
                    </ListItem>
                    <ListItem className={classes.listItem}>
                        <div className={classes.listItemSubContainer}>
                            <img alt="Facebook" className={classes.socialIcons} src={FacebookLogo} />
                            <a href={encodedFacebookUrl} rel="noreferrer" target="_blank">Facebook</a>
                        </div>
                    </ListItem>
                    <ListItem className={classes.listItem}>
                        <div className={classes.listItemSubContainer}>
                            <img alt="Twitter" className={classes.socialIcons} src={TwitterLogo} />
                            <a href={encodedTwitterUrl} rel="noreferrer" target="_blank" className="twitter-share-button" data-show-count="false">Twitter</a>
                        </div>
                    </ListItem>
                </List>
            </div>
            {
                (sharedItem.videoPath && sharedItem.videoPath.length > 0) ?
                    <div style={{ marginBottom: 20 }}>
                        <Typography style={{ marginBottom: 8 }} variant="subtitle2">Your video message</Typography>
                        <div>
                            <div>
                                <Button style={{ width: 120 }} disabled={disabled} onClick={() => setShowLinkVideo(true)} size="small" variant="contained" color="primary">
                                    Change Video
                                </Button>
                                &nbsp;
                                <Button style={{ width: 120 }} disabled={disabled} onClick={() => setShowRemoveVideo(true)} size="small" variant="contained" color="primary">
                                    Remove Video
                                </Button>
                            </div>
                        </div>
                    </div> :
                    <div style={{ marginBottom: 20 }}>
                        <Typography style={{ marginBottom: 8 }} variant="subtitle2">Your video message&nbsp;<a href="https://sermonmaker.sermon.stream/share-your-video" rel="noopener noreferrer" target="_blank">See an example</a></Typography>
                        <div>
                            <div>
                                <Button style={{ width: 120 }} disabled={disabled} onClick={() => setShowLinkVideo(true)} size="small" variant="contained" color="primary">
                                    Add a Video
                                </Button>
                            </div>
                        </div>
                    </div>
            }
            {!disabled &&
                <>
                    <div style={{ marginBottom: 6 }}>
                        <Typography style={{ marginBottom: 8 }} variant="subtitle2">Your shared page</Typography>
                    </div>
                    <div>
                        <div>
                            <a style={{ textDecoration: 'none' }} rel="noopener noreferrer" target="_blank" href={sharedUrl}>
                                <Button style={{ width: 120 }} disabled={disabled} size="small" variant="contained" color="primary">
                                    Preview
                                </Button>
                            </a>

                        </div>
                    </div>
                </>
            }
            <FormControl style={{ marginTop: 16 }}>
                <FormControlLabel
                    style={{ marginLeft: 0 }}
                    label={`Comments are ${sharedItem.commentsEnabled ? "on" : "off"}`}
                    className={classes.mobileFormLabel}
                    control={
                        <Switch
                            checked={sharedItem.commentsEnabled}
                            onChange={() => handleCommentsToggle()}
                            value="Share"
                            color="primary"
                        />
                    }
                    classes={{ label: classes.switchLabel }}
                    labelPlacement="start"
                />
            </FormControl>
            <RemoveVideoDialog isOpen={showRemoveVideo} onClose={() => setShowRemoveVideo(false)} />
            <ShareVideoDialog isOpen={showLinkVideo} onClose={() => setShowLinkVideo(false)} />
        </DialogContent>
    );
}